import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ActionTypes } from "@/actions";
import { useAuth } from "../auth/AuthContext";
import { actionsAPI, ActionDefinition, ActionOperationType } from "../api/actionsApi";
import { ComponentWithSource } from "../api/componentsData";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { EMPTY_ARRAY } from "@/constants/reduxStableRefs";

const DEFAULT_AMOUNT = "1";

function isBooleanLikeField(fieldDef: any): boolean {
    if (!fieldDef) return false;
    const t = String(fieldDef.type ?? "").toLowerCase().trim();
    return t === "boolean" || t === "checkbox";
}

/** Fields where the stored value can be cleared (unset / empty in Mongo). */
function canFieldBeUnset(fieldDef: any): boolean {
    if (!fieldDef) return false;
    const t = String(fieldDef.type ?? "").toLowerCase().trim();
    if (t === "select" || t === "dropdown") {
        return fieldDef.allowEmptyOption !== false;
    }
    return (
        t === "text" ||
        t === "textarea" ||
        t === "richtext" ||
        t === "code" ||
        t === "hidden" ||
        t === "url" ||
        t === "email" ||
        t === "datetime" ||
        t === "dateonly" ||
        t === "timeonly" ||
        t === "calendar" ||
        t === "date" ||
        t === "relation" ||
        t === "systemrelation" ||
        t === "multiplerelation" ||
        t === "file" ||
        t === "image"
    );
}

const Actions = () => {
    const dispatch = useDispatch();
    const reduxUser = useSelector((state: any) => state.user);
    const { user: authUser } = useAuth();
    const user = authUser || reduxUser;
    const components = useSelector((state: any) => state.components.data ?? EMPTY_ARRAY);

    const [actions, setActions] = useState<ActionDefinition[]>([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const [editingUid, setEditingUid] = useState<string | null>(null);
    const [name, setName] = useState("");
    const [componentSlug, setComponentSlug] = useState("");
    const [field, setField] = useState("");
    const [operationType, setOperationType] = useState<ActionOperationType>("increment");
    const [amount, setAmount] = useState(DEFAULT_AMOUNT);
    const [setValue, setSetValue] = useState("");

    useEffect(() => {
        if (user) {
            dispatch({ type: ActionTypes.COMPONENTS_REQUEST, payload: { user } });
        }
    }, [dispatch, user]);

    const selectedComponent = useMemo(() => {
        return components.find((component: ComponentWithSource) => component.slug === componentSlug);
    }, [components, componentSlug]);

    const availableFields = useMemo(() => {
        if (!selectedComponent?.fields) {
            return [];
        }
        return selectedComponent.fields.filter((fieldDef: any) => fieldDef?.name);
    }, [selectedComponent]);

    const selectedField = useMemo(() => {
        return availableFields.find((fieldDef: any) => fieldDef.name === field);
    }, [availableFields, field]);

    const parseSelectOptions = (value: any): string[] => {
        if (!value) return [];
        if (Array.isArray(value)) {
            return value.map((option) => String(option).trim()).filter(Boolean);
        }
        if (typeof value === 'string') {
            return value.split(',').map((option) => option.trim()).filter(Boolean);
        }
        return [];
    };

    const selectedFieldOptions = useMemo(() => {
        if (!selectedField) {
            return [];
        }
        const options = parseSelectOptions(selectedField.options);
        return options;
    }, [selectedField]);

    const setUsesBooleanControl = operationType === "set" && isBooleanLikeField(selectedField);
    const clearOperationAllowed = Boolean(selectedField && canFieldBeUnset(selectedField));

    useEffect(() => {
        if (!setUsesBooleanControl) return;
        setSetValue((prev) => (prev === "true" || prev === "false" ? prev : "false"));
    }, [setUsesBooleanControl, field, selectedField?.type]);

    useEffect(() => {
        if (operationType === "clear" && !clearOperationAllowed) {
            setOperationType("set");
        }
    }, [operationType, clearOperationAllowed]);

    const loadActions = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await actionsAPI.listActions();
            setActions(Array.isArray(response.data) ? response.data : []);
        } catch (loadError: any) {
            setError(loadError?.message || "Failed to load actions.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadActions();
    }, [loadActions]);

    const resetForm = () => {
        setEditingUid(null);
        setName("");
        setComponentSlug("");
        setField("");
        setOperationType("increment");
        setAmount(DEFAULT_AMOUNT);
        setSetValue("");
    };

    const handleEdit = (action: ActionDefinition) => {
        setEditingUid(action.uid || null);
        setName(action.name || "");
        setComponentSlug(action.componentSlug || "");
        setField(action.operation?.field || "");
        setOperationType(action.operation?.type || "increment");
        setAmount(action.operation?.amount !== undefined ? String(action.operation.amount) : DEFAULT_AMOUNT);
        setSetValue(
            action.operation?.type === "clear"
                ? ""
                : action.operation?.value !== undefined
                    ? String(action.operation.value)
                    : ""
        );
    };

    const handleDelete = async (uid?: string) => {
        if (!uid) {
            return;
        }
        try {
            setSaving(true);
            await actionsAPI.deleteAction(uid);
            await loadActions();
        } catch (deleteError: any) {
            setError(deleteError?.message || "Failed to delete action.");
        } finally {
            setSaving(false);
        }
    };

    const validateForm = (): string | null => {
        if (!name.trim()) {
            return "Action name is required.";
        }
        if (!componentSlug) {
            return "Collection is required.";
        }
        if (!field) {
            return "Field is required.";
        }
        if (operationType === "increment" || operationType === "decrement") {
            const parsedAmount = Number(amount);
            if (!Number.isFinite(parsedAmount)) {
                return "Amount must be a valid number.";
            }
        }
        if (operationType === "set") {
            if (setValue === undefined || setValue === null || String(setValue).trim() === "") {
                return "Set value is required.";
            }
        }
        if (operationType === "clear" && !clearOperationAllowed) {
            return "This field cannot be cleared.";
        }
        return null;
    };

    const buildPayload = (): ActionDefinition => {
        const payload: ActionDefinition = {
            name: name.trim(),
            componentSlug,
            operation: {
                type: operationType,
                field: field
            }
        };

        if (operationType === "increment" || operationType === "decrement") {
            const parsedAmount = Number(amount);
            payload.operation.amount = Number.isFinite(parsedAmount) ? parsedAmount : 1;
        }

        if (operationType === "set") {
            let value: string | number | boolean = setValue;
            if (isBooleanLikeField(selectedField)) {
                value = setValue === "true";
            } else if (String(selectedField?.type ?? "").toLowerCase().trim() === "number") {
                const parsedValue = Number(setValue);
                value = Number.isFinite(parsedValue) ? parsedValue : setValue;
            }
            payload.operation.value = value;
        }

        return payload;
    };

    const handleSubmit = async () => {
        const validationError = validateForm();
        if (validationError) {
            setError(validationError);
            return;
        }

        try {
            setSaving(true);
            setError(null);
            const payload = buildPayload();
            if (editingUid) {
                await actionsAPI.updateAction(editingUid, payload);
            } else {
                await actionsAPI.createAction(payload);
            }
            await loadActions();
            resetForm();
        } catch (saveError: any) {
            setError(saveError?.message || "Failed to save action.");
        } finally {
            setSaving(false);
        }
    };

    const renderOperationSummary = (action: ActionDefinition) => {
        if (!action?.operation) {
            return "No operation";
        }
        if (action.operation.type === "increment") {
            return `Increase ${action.operation.field} by ${action.operation.amount ?? 1}`;
        }
        if (action.operation.type === "decrement") {
            return `Decrease ${action.operation.field} by ${action.operation.amount ?? 1}`;
        }
        if (action.operation.type === "set") {
            return `Set ${action.operation.field} to ${action.operation.value ?? ""}`;
        }
        if (action.operation.type === "clear") {
            return `Clear ${action.operation.field}`;
        }
        return "Unknown operation";
    };

    return (
        <div className="p-6 space-y-6">
            <Card>
                <CardHeader>
                    <CardTitle>Actions</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                    {error && (
                        <div className="text-sm text-red-600">{error}</div>
                    )}
                    <div className="grid gap-4 md:grid-cols-2">
                        <div className="space-y-2">
                            <Label htmlFor="action-name">Action name</Label>
                            <Input
                                id="action-name"
                                value={name}
                                onChange={(event) => setName(event.target.value)}
                                placeholder="Add 1 to grocery list"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Collection</Label>
                            <Select value={componentSlug} onValueChange={(value) => {
                                setComponentSlug(value);
                                setField("");
                            }}>
                                <SelectTrigger>
                                    <SelectValue placeholder="Select collection" />
                                </SelectTrigger>
                                <SelectContent>
                                    {components.map((component: ComponentWithSource) => (
                                        <SelectItem key={component.slug} value={component.slug}>
                                            {component.name || component.slug}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label>Field</Label>
                            <Select value={field} onValueChange={setField} disabled={!componentSlug}>
                                <SelectTrigger>
                                    <SelectValue placeholder="Select field" />
                                </SelectTrigger>
                                <SelectContent>
                                    {availableFields.map((fieldDef: any) => (
                                        <SelectItem key={fieldDef.name} value={fieldDef.name}>
                                            {fieldDef.label || fieldDef.name}
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>
                        <div className="space-y-2">
                            <Label>Operation</Label>
                            <Select value={operationType} onValueChange={(value) => setOperationType(value as ActionOperationType)}>
                                <SelectTrigger>
                                    <SelectValue placeholder="Select operation" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="increment">Increment</SelectItem>
                                    <SelectItem value="decrement">Decrement</SelectItem>
                                    <SelectItem value="set">Set value</SelectItem>
                                    {clearOperationAllowed && (
                                        <SelectItem value="clear">Clear value</SelectItem>
                                    )}
                                </SelectContent>
                            </Select>
                        </div>
                        {(operationType === "increment" || operationType === "decrement") && (
                            <div className="space-y-2">
                                <Label htmlFor="action-amount">Amount</Label>
                                <Input
                                    id="action-amount"
                                    type="number"
                                    value={amount}
                                    onChange={(event) => setAmount(event.target.value)}
                                />
                            </div>
                        )}
                        {operationType === "set" && (
                            <div className="space-y-2">
                                <Label htmlFor="action-set-value">Set value</Label>
                                {setUsesBooleanControl && (
                                    <Select value={setValue === "true" || setValue === "false" ? setValue : "false"} onValueChange={setSetValue}>
                                        <SelectTrigger id="action-set-value">
                                            <SelectValue placeholder="Select true or false" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="true">True</SelectItem>
                                            <SelectItem value="false">False</SelectItem>
                                        </SelectContent>
                                    </Select>
                                )}
                                {!setUsesBooleanControl && selectedFieldOptions.length > 0 && (
                                    <Select value={setValue} onValueChange={setSetValue}>
                                        <SelectTrigger id="action-set-value">
                                            <SelectValue placeholder="Select value" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {selectedFieldOptions.map((option: string) => (
                                                <SelectItem key={option} value={option}>
                                                    {option}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                )}
                                {!setUsesBooleanControl && selectedFieldOptions.length === 0 && (
                                    <Input
                                        id="action-set-value"
                                        value={setValue}
                                        onChange={(event) => setSetValue(event.target.value)}
                                        placeholder="Enter a value"
                                    />
                                )}
                            </div>
                        )}
                    </div>
                    <div className="flex gap-2">
                        <Button onClick={handleSubmit} disabled={saving}>
                            {editingUid ? "Update action" : "Create action"}
                        </Button>
                        {editingUid && (
                            <Button variant="outline" onClick={resetForm} disabled={saving}>
                                Cancel edit
                            </Button>
                        )}
                    </div>
                </CardContent>
            </Card>

            <Card>
                <CardHeader>
                    <CardTitle>Your actions</CardTitle>
                </CardHeader>
                <CardContent>
                    {loading && <div className="text-sm text-gray-500">Loading actions...</div>}
                    {!loading && actions.length === 0 && (
                        <div className="text-sm text-gray-500">No actions created yet.</div>
                    )}
                    {!loading && actions.length > 0 && (
                        <div className="space-y-3">
                            {actions.map((action) => (
                                <div
                                    key={action.uid}
                                    className="flex flex-col md:flex-row md:items-center md:justify-between gap-2 rounded-md border p-3"
                                >
                                    <div>
                                        <div className="font-medium text-gray-900">{action.name}</div>
                                        <div className="text-sm text-gray-600">
                                            {action.componentSlug} • {renderOperationSummary(action)}
                                        </div>
                                    </div>
                                    <div className="flex gap-2">
                                        <Button variant="outline" onClick={() => handleEdit(action)} disabled={saving}>
                                            Edit
                                        </Button>
                                        <Button variant="destructive" onClick={() => handleDelete(action.uid)} disabled={saving}>
                                            Delete
                                        </Button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
};

export default Actions;
